import { useMemo, useState } from 'react';
import { AgentJSONAPI } from '~/api/resources/agent';
import { LoanOfficerJSONAPI } from '~/api/resources/loan_officer';
import { OpportunityJSONAPI } from '~/api/resources/opportunity';
import { TeamMemberJSONAPI } from '~/api/resources/team_member';

const SELECTED_ROWS_LIMIT = 500;

type RowData = LoanOfficerJSONAPI | AgentJSONAPI | TeamMemberJSONAPI | OpportunityJSONAPI;

type Args = {
  rows: RowData[];
  rowDataType?: 'loanOfficers' | 'realtors' | 'opportunities';
};

export type RowsData = {
  id: string;
  addedId: string | undefined;
  synced_with_crm: boolean | undefined;
};

export type SelectedRowsProps = {
  rows: string[];
  rowsData: RowsData[];
  totalLoanVolume: number;
  onSelectAllRows: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSelectRow: (
    id: string,
    loanVolume: number,
    addedId: string | undefined,
    synced_with_crm: boolean | undefined
  ) => void;
  isSelectedRow: (id: string) => boolean;
  isIndeterminate: boolean;
  isChecked: boolean;
  clear: () => void;
  limit: number;
  isDisabledHeadCheckbox: boolean;
};

const useSelectedRows = (args: Args): SelectedRowsProps => {
  const { rowDataType } = args;
  const aggregateField =
    rowDataType === 'realtors'
      ? 'sale_amount'
      : rowDataType === 'opportunities'
      ? 'loan_amount'
      : 'loan_volume';
  const [rows, setRows] = useState<string[]>([]);
  const [rowsData, setRowsData] = useState<RowsData[]>([]);
  const [totalLoanVolume, setTotalLoanVolume] = useState(0);

  const clear = () => {
    setRows([]);
    setRowsData([]);
    setTotalLoanVolume(0);
  };

  const partContains = (where: string[], what: RowData[]) => {
    for (let i = 0; i < what.length; i++) {
      if (where.indexOf(what[i].id) !== -1) return true;
    }
    return false;
  };

  const contains = (where: string[], what: RowData[]) => {
    for (let i = 0; i < what.length; i++) {
      if (where.indexOf(what[i].id) == -1) return false;
    }
    return true;
  };

  const isChecked = useMemo(
    () => args.rows.length > 0 && contains(rows, args.rows),
    [args.rows, rows]
  );
  const isIndeterminate = useMemo(
    () => rows.length > 0 && partContains(rows, args.rows) && !contains(rows, args.rows),
    [rows, args.rows]
  );

  const onSelectAllRows = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked && !isIndeterminate) {
      const ids: string[] = [];
      const addedData: RowsData[] = [];
      let loanVolume = 0;
      // TODO: Try to specify row type exactly basing on typeof rowDataType
      args.rows.forEach((row: any) => {
        if (rows.indexOf(row.id) === -1) {
          ids.push(row.id);
          loanVolume += row.attributes[aggregateField];
          addedData.push({
            id: row.id,
            addedId: row.attributes.added?.user_id,
            synced_with_crm: row.attributes?.synced_with_bpd_crm,
          });
        }
      });

      setRows((prevValue) => prevValue.concat(ids));
      setRowsData((prevValue) => prevValue.concat(addedData));
      setTotalLoanVolume((prevValue) => prevValue + loanVolume);
    } else {
      const ids: string[] = [];
      const addedData: RowsData[] = [];
      let loanVolume = 0;
      args.rows.forEach((row: any) => {
        if (rows.indexOf(row.id) !== -1) {
          ids.push(row.id);
          loanVolume += row.attributes[aggregateField];
          addedData.push({
            id: row.id,
            addedId: row.attributes.added?.user_id,
            synced_with_crm: row.attributes?.synced_with_bpd_crm,
          });
        }
      });

      setRows((prevValue) => prevValue.filter((item) => ids.indexOf(item) === -1));
      setRowsData((prevValue) => prevValue.filter((item) => ids.indexOf(item.id) === -1));
      setTotalLoanVolume((prevValue) => prevValue - loanVolume);
      if (rowDataType === 'opportunities') clear();
    }
  };

  const onSelectRow = (
    id: string,
    loanVolume: number,
    addedId: string | undefined,
    synced_with_crm: boolean | undefined
  ) => {
    const selectedIndex = rows.indexOf(id);

    setRows((prevValue) => {
      const newSelected: string[] = [];
      if (selectedIndex === -1) {
        return newSelected.concat(prevValue, id);
      } else if (selectedIndex === 0) {
        return newSelected.concat(prevValue.slice(1));
      } else if (selectedIndex === prevValue.length - 1) {
        return newSelected.concat(prevValue.slice(0, -1));
      } else if (selectedIndex > 0) {
        return newSelected.concat(
          prevValue.slice(0, selectedIndex),
          prevValue.slice(selectedIndex + 1)
        );
      }
      return newSelected;
    });

    setRowsData((prevValue) => {
      const newSelected: RowsData[] = [];
      if (selectedIndex === -1) {
        return newSelected.concat(prevValue, {
          id: id,
          addedId: addedId,
          synced_with_crm: synced_with_crm,
        });
      } else if (selectedIndex === 0) {
        return newSelected.concat(prevValue.slice(1));
      } else if (selectedIndex === prevValue.length - 1) {
        return newSelected.concat(prevValue.slice(0, -1));
      } else if (selectedIndex > 0) {
        return newSelected.concat(
          prevValue.slice(0, selectedIndex),
          prevValue.slice(selectedIndex + 1)
        );
      }
      return newSelected;
    });

    setTotalLoanVolume((prevValue) =>
      selectedIndex === -1 ? prevValue + loanVolume : prevValue - loanVolume
    );
  };

  const isSelectedRow = (id: string) => rows.indexOf(id) !== -1;

  const getCountOfCheckedInCurrentPage = (where: string[], what: RowData[]): number =>
    what.reduce((sum: number, { id }) => (where.includes(id) ? sum + 1 : sum), 0);

  const isDisabledHeadCheckbox = useMemo(
    () =>
      !isIndeterminate &&
      rows.length + (args.rows.length - getCountOfCheckedInCurrentPage(rows, args.rows)) >
        SELECTED_ROWS_LIMIT,
    [isIndeterminate, rows, args.rows]
  );

  return {
    rows,
    rowsData,
    totalLoanVolume,
    onSelectAllRows,
    onSelectRow,
    isSelectedRow,
    isIndeterminate,
    isChecked,
    clear,
    limit: SELECTED_ROWS_LIMIT,
    isDisabledHeadCheckbox,
  };
};

export default useSelectedRows;
